import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { supabase } from "@/lib/supabase";
import { deleteAccountCopy } from "@/components/delete-account/copy";
import { LoginStep } from "@/components/delete-account/LoginStep";
import { ConfirmStep } from "@/components/delete-account/ConfirmStep";

const DeleteAccount = () => {
  const { language, getLocalizedPath } = useLanguage();
  const copy = deleteAccountCopy[language];

  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);

  useEffect(() => {
    document.title =
      language === "fr" ? "Supprimer mon compte | ReFound" : "Delete my account | ReFound";
  }, [language]);

  useEffect(() => {
    // Initial session (also picks up the OAuth redirect from Google/Apple)
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoaded(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSessionLoaded(true);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  return (
    <Layout>
      <main className="pt-28 pb-16 sm:pt-32 sm:pb-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <article className="max-w-xl mx-auto">
            <header className="text-center mb-10">
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
                {copy.title}
              </h1>
              <p className="text-lg text-muted-foreground">{copy.subtitle}</p>
            </header>

            {done ? (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center space-y-5">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-600">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">
                    {copy.done.title}
                  </h2>
                  <p className="text-muted-foreground">{copy.done.desc}</p>
                  <Alert className="border-amber-500/50 bg-amber-500/10 text-left">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm">
                      {copy.done.subscriptionReminder}
                    </AlertDescription>
                  </Alert>
                  <Button asChild variant="outline">
                    <a href={getLocalizedPath("")}>{copy.done.backHome}</a>
                  </Button>
                </CardContent>
              </Card>
            ) : !sessionLoaded ? (
              <Card className="bg-card border-border">
                <CardContent className="p-8 space-y-4">
                  <Skeleton className="h-6 w-2/3" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ) : session ? (
              <ConfirmStep
                copy={copy}
                language={language}
                email={session.user.email ?? session.user.id}
                onDeleted={() => setDone(true)}
              />
            ) : (
              <LoginStep copy={copy} />
            )}
          </article>
        </div>
      </main>
    </Layout>
  );
};

export default DeleteAccount;
